import EditorialLayout, { editorialStyles as styles } from '../../components/EditorialLayout'

export default function HowOnlineTunerWorks() {
  return (
    <EditorialLayout
      title='How an Online Guitar Tuner Works'
      description='See how a browser turns microphone audio into a detected frequency, finds the nearest musical note, and tells you whether a string is flat or sharp.'
      path='/guides/how-online-guitar-tuner-works'
      eyebrow='Inside the tuner'
      updated='August 10, 2026'
      article
    >
      <p>
        An online tuner does not “hear” a note as a person does. It receives a
        rapidly changing audio waveform, estimates how often that waveform
        repeats each second, and compares the resulting frequency with a table
        of musical notes.
      </p>

      <div className={styles.flow} aria-label='Pitch detection process'>
        <div className={styles.flowStep}>Microphone signal</div>
        <div className={styles.flowArrow} aria-hidden='true'>→</div>
        <div className={styles.flowStep}>Audio samples</div>
        <div className={styles.flowArrow} aria-hidden='true'>→</div>
        <div className={styles.flowStep}>Pitch estimate</div>
        <div className={styles.flowArrow} aria-hidden='true'>→</div>
        <div className={styles.flowStep}>Nearest note</div>
        <div className={styles.flowArrow} aria-hidden='true'>→</div>
        <div className={styles.flowStep}>Flat / in tune / sharp</div>
      </div>

      <h2>1. The browser receives microphone audio</h2>
      <p>
        After you grant permission, the browser’s Web Audio API creates a live
        audio stream. Guitar-Tuner.org analyses that stream locally. The tuner
        does not need to upload or store a recording in order to estimate pitch.
      </p>
      <p>
        Audio is represented as a sequence of sample values. Instead of
        inspecting an endless stream at once, the tuner works with short blocks.
        This site currently uses blocks of 4,096 samples: long enough to reveal
        repeating low-frequency guitar waves while remaining responsive.
      </p>

      <h2>2. Pitch detection finds repetition</h2>
      <p>
        A stable musical sound repeats in a roughly periodic way. If one cycle
        repeats 110 times per second, the fundamental frequency is 110 Hz — the
        target frequency for A2. Real strings also produce harmonics at
        multiples of that fundamental, which give an instrument its tone and
        make pitch detection more challenging than simply locating the loudest
        frequency.
      </p>
      <p>
        Guitar-Tuner.org uses the Aubio audio analysis library to estimate pitch.
        It also rejects readings that are too quiet, outside the instrument
        range, or unstable across consecutive analysis windows. This reduces
        jumps caused by room noise and the sharp transient at the instant a
        string is plucked.
      </p>

      <div className={styles.example}>
        <h3>Example: detecting the open A string</h3>
        <p>
          Suppose three stable analysis windows estimate 109.7, 109.9, and
          109.8 Hz. The measurements are close together, so the tuner accepts
          the signal and compares approximately 109.8 Hz with the target notes.
          A2 at 110 Hz is the nearest match.
        </p>
      </div>

      <h2>3. Frequency becomes a musical note</h2>
      <p>
        Notes in equal temperament follow a logarithmic pattern. Each step of
        one semitone multiplies frequency by the twelfth root of two. The tuner
        can calculate the nearest note relative to A4 = 440 Hz using:
      </p>
      <div className={styles.callout}>
        <p><code>note distance = 12 × log₂(measured frequency ÷ 440)</code></p>
        <p>
          Rounding that distance to the nearest whole semitone identifies the
          closest note; the remaining fraction becomes the tuning error.
        </p>
      </div>

      <h2>4. Cents describe the remaining error</h2>
      <p>
        The closest note name is only part of the answer. A string at 109.8 Hz
        and one at 111 Hz may both be identified as A2, but the first is slightly
        flat and the second is sharp. Cents express that distance consistently:
      </p>
      <p><code>cents = 1200 × log₂(measured frequency ÷ target frequency)</code></p>
      <table>
        <thead>
          <tr><th>A2 measurement</th><th>Difference</th><th>Instruction</th></tr>
        </thead>
        <tbody>
          <tr><td>109.0 Hz</td><td>about −16 cents</td><td>Tighten the string</td></tr>
          <tr><td>110.0 Hz</td><td>0 cents</td><td>In tune</td></tr>
          <tr><td>111.0 Hz</td><td>about +16 cents</td><td>Loosen the string</td></tr>
        </tbody>
      </table>

      <h2>Why a tuner display sometimes jumps</h2>
      <ul>
        <li><strong>The attack is sharp:</strong> a hard pluck temporarily stretches the string.</li>
        <li><strong>Harmonics dominate:</strong> the detector briefly locks onto an overtone instead of the fundamental.</li>
        <li><strong>Several sounds overlap:</strong> other strings or room noise create competing periodic signals.</li>
        <li><strong>The signal is weak:</strong> distance from the microphone makes the note difficult to distinguish from noise.</li>
        <li><strong>The note is decaying:</strong> as volume falls, the estimate becomes less stable.</li>
      </ul>

      <h2>How to give the tuner a clean signal</h2>
      <ol>
        <li>Mute the strings you are not tuning.</li>
        <li>Place the instrument reasonably close to the microphone.</li>
        <li>Pluck once with moderate force and let the note ring.</li>
        <li>Wait a fraction of a second for the attack to settle.</li>
        <li>Make small adjustments and pluck again rather than turning the peg continuously.</li>
      </ol>

      <h2>Microphone mode versus tuning by ear</h2>
      <p>
        Microphone mode measures pitch and is the quickest option when you need
        an objective target. By-ear mode plays a reference note but leaves the
        comparison to you. Practising by ear can improve pitch awareness, while
        automatic mode is better for checking precise intonation before playing.
      </p>

      <h2>Frequently asked questions</h2>
      <h3>Does the tuner send microphone audio to a server?</h3>
      <p>
        No. Pitch analysis for this tuner happens inside your browser. Your
        browser still controls microphone permission, which you can remove in
        its site settings.
      </p>
      <h3>Why does the tuner show the wrong string?</h3>
      <p>
        The string may be far from its target, or the detector may be following
        a strong harmonic. Select the intended string manually, mute the others,
        and compare the displayed octave and frequency with the expected target.
      </p>

      <div className={styles.related}>
        <h2>Continue learning</h2>
        <p>
          Keep the <a href='/guides/guitar-string-frequency-chart'>standard guitar frequency chart</a>{' '}
          nearby, or diagnose <a href='/guides/why-guitar-goes-out-of-tune'>why a correctly tuned guitar may drift again</a>.
        </p>
      </div>
    </EditorialLayout>
  )
}
